import ClassNames from "classnames";
import { FunctionComponent, useEffect, useState } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { injectScript, loadScript } from "../../slices/loadStates.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/mts-tours-module.module.css";
import { BaseModuleProps, MTSToursModuleSettings } from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

type Props = BaseModuleProps<MTSToursModuleSettings>;

const MTSToursModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    translation: {
      languageId,
      settings: { title, subtitle },
    },
    settings: { user, archive, textAlign },
  },
  translatedModule,
  isFirstOnPage,
}) => {
  const dispatch = useAppDispatch();
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const [isLazyloaded, setIsLazyloaded] = useState(false);

  const tmtsLanguage = getSupportedLanguage(languageId, ["de", "it", "en"]);
  const scriptUrl = `https://tours.t-mts.com/${user}/${archive}/api.js`;

  useEffect(() => {
    isLazyloaded && user && archive && dispatch(injectScript(scriptUrl));
  }, [user, archive, isLazyloaded]);

  // The script needs to be loaded again and the widget script removed,
  // otherwise the widget won’t load again when navigating pages in the CMS
  useEffect(
    () => () => {
      dispatch(loadScript({ url: scriptUrl, status: "unloaded" }));
      const scriptToRemove = document.querySelector(
        `#js-${user}-${archive}-tmts-widget`,
      );

      scriptToRemove &&
        scriptToRemove.parentNode &&
        scriptToRemove.parentNode.removeChild(scriptToRemove);
    },
    [],
  );

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.MTSToursModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>
      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <div
          className={ClassNames(moduleBaseStyles.Module__Wrapper, {
            [styles["MTSToursModule__Content--empty"]]: !(user && archive),
          })}
        >
          {user && archive && (
            <div
              data-tmts-user={user}
              data-tmts-archive={archive}
              data-tmts-language={tmtsLanguage}
            ></div>
          )}
        </div>
      </LazyloadWrapper>
    </ModuleWithHeadings>
  );
};

export default MTSToursModule;
